import '@fpsm/cxy-tokens/tokens.css';

import { type ReactNode } from 'react';
import { type BreadcrumbListSchema } from '@cx-web/api-clients/template-api/types';
import { footerItemType, type FooterV2Response, navItemType } from '@cx-web/domain-types';
import classnames from 'classnames';

import { type Repub } from '@cx-modules/constants';

import Breadcrumbs, { footerRendersBreadcrumbs } from '../../page-template/Breadcrumbs';

import FooterGeneralLinks from './components/FooterGeneralLinks/index';
import FooterMeta from './components/FooterMeta/index';
import FooterNav from './components/FooterNav/index';
import styles from './FooterV2.module.css';
import { type CmpProps, type SocialLink } from './types';

export type FooterV2Props = {
  /** Curated JSON-LD BreadcrumbList for the in-footer breadcrumbs (OCGC-3445); derived from `path` when absent. */
  breadcrumbsDataSchema?: BreadcrumbListSchema;
  cmpProps?: CmpProps;
  footerData?: FooterV2Response;
  isAdmiralCookieBannerEnabled: boolean;
  isUkFooterStyle?: boolean;
  legalAge: string;
  legalText: string;
  logo: ReactNode;
  /** Repub-prefixed request path feeding the in-footer breadcrumbs (OCGC-3445); optional, see the doc. */
  path?: string;
  /** Current repub — drives the breadcrumb link prefix (OCGC-3445). Optional, see `path`. */
  repub?: Repub;
  socialLinks: SocialLink[];
};

export const FooterV2 = ({
  breadcrumbsDataSchema,
  cmpProps,
  footerData,
  isAdmiralCookieBannerEnabled,
  isUkFooterStyle,
  legalAge,
  legalText,
  logo,
  path,
  repub,
  socialLinks,
}: FooterV2Props) => {
  // OCGC-3445: under the UK footer style the breadcrumbs render here (above the nav) instead of the page
  // body, gated on the shared `footerRendersBreadcrumbs` predicate. Optional `path`/`repub` mean non-UK
  // callers (product-plus) render nothing. See doc/architecture/OCGC-3445-footer-breadcrumbs.md.
  const breadcrumbs =
    path && repub && footerRendersBreadcrumbs({ isUkFooterStyle, path, repub }) ? (
      <Breadcrumbs dataSchema={breadcrumbsDataSchema} isUkFooterStyle path={path} repub={repub} />
    ) : null;

  return (
    <footer
      className={classnames(styles.footerContainer, isUkFooterStyle && styles.footerContainerUk)}
      data-testid="footer-v2"
    >
      {breadcrumbs}
      <FooterNav
        groupType={navItemType.GROUP}
        isUkFooterStyle={isUkFooterStyle}
        levelOne={footerData?.levelOne ?? []}
      />
      <FooterMeta
        isUkFooterStyle={isUkFooterStyle}
        legalAge={legalAge}
        legalText={legalText}
        levelTwo={footerData?.levelTwo ?? []}
        logo={logo}
        logoType={footerItemType.LOGO}
        socialLinks={socialLinks}
      />
      <FooterGeneralLinks
        cmpProps={cmpProps}
        isAdmiralCookieBannerEnabled={isAdmiralCookieBannerEnabled}
        isUkFooterStyle={isUkFooterStyle}
        levelThree={footerData?.levelThree ?? []}
      />
    </footer>
  );
};
